import React from "react";
import { FaEnvelopeOpenText, FaInbox } from "react-icons/fa";

const NewsArticlePage = ({ question, value, onChange }) => {
  const { questionId, article = {}, email = null, acknowledgmentLabel } = question;
  const requiresAck = question.required;
  const ackLabel = acknowledgmentLabel || "I have read the content above";

  const {
    publication = "The Global Tribune",
    headline = "",
    subtitle = "",
    author = "",
    authorTitle = "",
    date = "",
    paragraphs = [],
    pullQuote = null,
    pullQuoteAfter = 2,
    figures = [],
    figuresAfter = null,
  } = article;

  // Renders the figure exhibit(s) as simple horizontal bar charts, styled to
  // match the newspaper card (light stone palette, serif title). Kept dependency
  // free (plain divs) so it stays glanceable, responsive, and deterministic.
  const renderFigures = (keyPrefix) => (
    <div key={keyPrefix} className="my-5 space-y-4 not-italic">
      {figures.map((fig, fi) => {
        const bars = fig.bars || [];
        const max = Math.max(1, ...bars.map((b) => Number(b.value) || 0));
        return (
          <figure
            key={`${keyPrefix}-${fig.id || fi}`}
            className="border border-stone-300 bg-stone-100 rounded p-4 sm:p-5"
          >
            <figcaption className="mb-3">
              {fig.label && (
                <div className="text-[10px] sm:text-xs font-sans font-semibold tracking-[0.15em] uppercase text-stone-500">
                  {fig.label}
                </div>
              )}
              {fig.title && (
                <div className="font-serif text-sm sm:text-base font-bold text-stone-900 leading-snug">
                  {fig.title}
                </div>
              )}
            </figcaption>
            <div>
              {bars.map((bar, bi) => {
                const pct = Math.round(((Number(bar.value) || 0) / max) * 100);
                const display = bar.display != null ? bar.display : bar.value;
                return (
                  <div key={bi} className="mb-2.5 last:mb-0">
                    <div className="flex items-baseline justify-between gap-3 text-xs sm:text-sm font-sans text-stone-700 mb-1">
                      <span className="leading-snug">{bar.label}</span>
                      <span className="font-semibold tabular-nums text-stone-900 flex-shrink-0">
                        {display}
                      </span>
                    </div>
                    <div
                      className="h-3 w-full bg-stone-200 rounded-sm overflow-hidden"
                      role="img"
                      aria-label={`${bar.label}: ${display}`}
                    >
                      <div
                        className={`h-full rounded-sm ${
                          bar.highlight ? "bg-stone-900" : "bg-stone-600"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {fig.credit && (
              <div className="mt-3 text-[10px] sm:text-xs text-stone-400 font-sans">
                {fig.credit}
              </div>
            )}
          </figure>
        );
      })}
    </div>
  );

  return (
    <div>
      {/* Official email card — involvement manipulation */}
      {email && (
        <div className="bg-white rounded-lg shadow-md border border-stone-200 overflow-hidden mb-6">
          {/* Inbox strip — frames the email as a found message in the participant's inbox */}
          <div className="bg-stone-800 text-stone-100 px-5 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaInbox className="w-3.5 h-3.5 text-stone-400" />
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-stone-300">
                Inbox
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-stone-400">
              <FaEnvelopeOpenText className="w-3 h-3" />
              <span>New message</span>
            </div>
          </div>
          {/* Email header */}
          <div className="bg-stone-100 border-b border-stone-200 px-5 py-3">
            <div className="space-y-1.5 text-sm">
              <div className="flex gap-2">
                <span className="text-stone-500 font-medium w-14 flex-shrink-0">From:</span>
                <span className="text-stone-800 font-semibold">{email.from}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-stone-500 font-medium w-14 flex-shrink-0">To:</span>
                <span className="text-stone-700">{email.to || "All enrolled students"}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-stone-500 font-medium w-14 flex-shrink-0">Subject:</span>
                <span className="text-stone-900 font-semibold">{email.subject}</span>
              </div>
              {email.date && (
                <div className="flex gap-2">
                  <span className="text-stone-500 font-medium w-14 flex-shrink-0">Date:</span>
                  <span className="text-stone-600">{email.date}</span>
                </div>
              )}
            </div>
          </div>
          {/* Email body */}
          <div className="px-5 py-4">
            <div className="text-sm text-stone-800 leading-relaxed space-y-3 whitespace-pre-line">
              {email.body}
            </div>
            {email.signature && (
              <div className="mt-4 pt-3 border-t border-stone-100 text-xs text-stone-500 whitespace-pre-line">
                {email.signature}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Newspaper card — light paper on dark background */}
      <div className="bg-stone-50 rounded-lg shadow-lg overflow-hidden border border-stone-200">
        {/* Masthead */}
        <div className="border-b-2 border-stone-800 mx-5 mt-5 pb-2">
          <div className="flex items-center justify-between">
            <div className="h-px flex-1 bg-stone-400" />
            <h2 className="font-serif text-base sm:text-lg tracking-[0.2em] uppercase text-stone-800 px-4 text-center font-bold">
              {publication}
            </h2>
            <div className="h-px flex-1 bg-stone-400" />
          </div>
          <div className="text-center mt-1">
            <span className="text-[10px] sm:text-xs text-stone-500 tracking-wider uppercase">
              {date}
            </span>
          </div>
        </div>

        {/* Article content */}
        <div className="px-5 sm:px-8 py-5">
          {/* Headline */}
          <h1 className="font-serif text-xl sm:text-2xl font-bold text-stone-900 leading-tight mb-2">
            {headline}
          </h1>

          {/* Subtitle / Deck */}
          {subtitle && (
            <p className="font-serif text-sm sm:text-base text-stone-600 italic leading-snug mb-4">
              {subtitle}
            </p>
          )}

          {/* Byline */}
          <div className="flex items-center gap-2 text-xs text-stone-500 mb-5 pb-4 border-b border-stone-200">
            <span>
              By <span className="font-semibold text-stone-700">{author}</span>
            </span>
            {authorTitle && (
              <>
                <span className="text-stone-300">|</span>
                <span>{authorTitle}</span>
              </>
            )}
          </div>

          {/* Body — drop cap on first paragraph, with pull quote and figures
              interleaved after their configured paragraph index */}
          <div className="font-serif text-[15px] text-stone-800 leading-relaxed space-y-3">
            {paragraphs.map((para, i) => {
              const blocks = [
                <p
                  key={`p-${i}`}
                  className={
                    i === 0
                      ? "first-letter:text-4xl first-letter:font-bold first-letter:float-left first-letter:mr-1 first-letter:mt-0.5 first-letter:leading-none first-letter:text-stone-900"
                      : ""
                  }
                >
                  {para}
                </p>,
              ];
              if (pullQuote && i + 1 === pullQuoteAfter) {
                blocks.push(
                  <blockquote
                    key={`pq-${i}`}
                    className="my-4 mx-2 sm:mx-6 py-3 px-4 border-l-4 border-stone-800 bg-stone-100 rounded-r"
                  >
                    <p className="font-serif text-base sm:text-lg italic text-stone-700 leading-snug">
                      {pullQuote.text}
                    </p>
                    {pullQuote.attribution && (
                      <footer className="mt-2 text-xs text-stone-500 font-sans">
                        — {pullQuote.attribution}
                      </footer>
                    )}
                  </blockquote>
                );
              }
              if (figures.length > 0 && figuresAfter !== null && i + 1 === figuresAfter) {
                blocks.push(renderFigures(`fig-${i}`));
              }
              return blocks;
            })}

            {/* Figures appended at the end when figuresAfter is unset or beyond the body */}
            {figures.length > 0 &&
              (figuresAfter === null || figuresAfter > paragraphs.length) &&
              renderFigures("fig-end")}
          </div>
        </div>

        {/* Footer rule */}
        <div className="mx-5 mb-4">
          <div className="h-px bg-stone-300" />
        </div>
      </div>

      {/* Acknowledgment checkbox — outside the newspaper card, in the survey's dark theme */}
      {requiresAck && (
        <label className="flex items-start gap-3 mt-5 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(questionId, e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-spiders-border accent-spiders-primary-500"
          />
          <span className="text-sm text-spiders-text-secondary">
            {ackLabel}
          </span>
        </label>
      )}
    </div>
  );
};

export default NewsArticlePage;
